import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../../utils/api';
import { STATUS } from '../../../utils/status';

export interface StageData {
  identifier: string;
  adjudication_points_earned: number | null;
  adjudication_points_total: number | null;
  adjudication_agreement_rate: number | null;
}

export interface AvailableStage {
  stage_id: number;
  stage_name: string;
  stage_category: string;
  is_adjudication: boolean;
}

export interface ErrorInsightStage {
  id: number;
  name: string;
  category: string;
}

interface ConfigAnalyticsState {
  configAnalytics: StageData[];
  adjudicationConfigAnalytics: any[];
  availableStages: AvailableStage[];
  errorInsightStages: ErrorInsightStage[];
  getConfigAnalyticsStatus: STATUS;
  getAdjudicationConfigAnalyticsStatus: STATUS;
  getAvailableStagesStatus: STATUS;
  getErrorInsightStagesStatus: STATUS;
  error: string | null;
}

const initialState: ConfigAnalyticsState = {
  configAnalytics: [],
  adjudicationConfigAnalytics: [],
  availableStages: [],
  errorInsightStages: [],
  getAdjudicationConfigAnalyticsStatus: STATUS.NOT_STARTED,
  getConfigAnalyticsStatus: STATUS.NOT_STARTED,
  getAvailableStagesStatus: STATUS.NOT_STARTED,
  getErrorInsightStagesStatus: STATUS.NOT_STARTED,
  error: null,
};

export const getErrorInsightStages = createAsyncThunk(
  'project/company/stage/error-insight/get',
  async (payload: { project_id: string; association_type: string }, thunkAPI) => {
    try {
      const { project_id, association_type } = payload;
      const queryString = new URLSearchParams({
        project_id,
        association_type,
      }).toString();

      const response = await api.get(`project/company/stage/?${queryString}`);
      const { status, data } = response;

      if (status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue({ response, data });
      }
    } catch (error: any) {
      if (error.response) {
        const { data } = error.response;
        const errorMessage = (data && data.message) ? data.message : 'Failed to fetch error insight stages';
        return thunkAPI.rejectWithValue({ message: errorMessage });
      } else if (error.request) {
        return thunkAPI.rejectWithValue({ message: 'No response received' });
      } else {
        return thunkAPI.rejectWithValue({ message: error.message });
      }
    }
  }
);

export const getAdjudicationConfigAnalytics = createAsyncThunk(
  'project/company/adjudication_config/analysis/get',
  async (payload: { project_id?: string }, thunkAPI) => {
    try {
      const { project_id } = payload;
      const queryString = new URLSearchParams({
        ...(project_id && { project_id: project_id }),
      }).toString();
      
      const response = await api.get(`/project/company/adjudication_config/analysis?${queryString}`);
      const { status, data } = response;
      
      if (status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue({ response, data });
      }
    } catch (error: any) {
      if (error.response) {
        const { data } = error.response;
        const errorMessage = (data && data.message) ? data.message : 'Failed to fetch adjudication config analytics';
        return thunkAPI.rejectWithValue({ message: errorMessage });
      } else if (error.request) {
        return thunkAPI.rejectWithValue({ message: 'No response received' });
      } else {
        return thunkAPI.rejectWithValue({ message: error.message });
      }
    }
  }
);

export const getStageConfigOverview = createAsyncThunk(
  'project/qa-analytics/overview/stage-config-overview/get',
  async (payload: {
    project_id?: string;
    stage_id?: string;
    search?: string;
    score_category?: string;
    modality?: string;
    page?: number;
    page_size?: number;
  }, thunkAPI) => {
    try {
      const { project_id, stage_id, search, score_category, modality, page, page_size } = payload;
      const queryString = new URLSearchParams({
        tab: 'config_analytics',
        chip: 'stage_wise',
        ...(project_id && { project_id }),
        ...(stage_id && { stage_id }),
        ...(search && { search }),
        ...(score_category && { score_category }),
        ...(modality && { modality }),
        ...(page && { page: String(page) }),
        ...(page_size && { page_size: String(page_size) }),
      }).toString();

      const response = await api.get(`project/qa-analytics/overview/?${queryString}`);
      const { status, data } = response;

      if (status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue({ response, data });
      }
    } catch (error: any) {
      if (error.response) {
        const { data } = error.response;
        const errorMessage = (data && data.message) ? data.message : 'Failed to fetch stage config overview';
        return thunkAPI.rejectWithValue({ message: errorMessage });
      } else if (error.request) {
        return thunkAPI.rejectWithValue({ message: 'No response received' });
      } else {
        return thunkAPI.rejectWithValue({ message: error.message });
      }
    }
  }
);

export const getConfigAnalyticsStages = createAsyncThunk(
  'project/qa-analytics/overview/config-stages/get',
  async (payload: { project_id?: string }, thunkAPI) => {
    try {
      const { project_id } = payload;
      const queryString = new URLSearchParams({
        tab: 'config_analytics',
        chip: 'stage_wise',
        ...(project_id && { project_id }),
      }).toString();

      const response = await api.get(`project/qa-analytics/overview/?${queryString}`);
      const { status, data } = response;

      if (status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue({ response, data });
      }
    } catch (error: any) {
      if (error.response) {
        const { data } = error.response;
        const errorMessage = (data && data.message) ? data.message : 'Failed to fetch config analytics stages';
        return thunkAPI.rejectWithValue({ message: errorMessage });
      } else if (error.request) {
        return thunkAPI.rejectWithValue({ message: 'No response received' });
      } else {
        return thunkAPI.rejectWithValue({ message: error.message });
      }
    }
  }
);

export const getConfigAnalytics = createAsyncThunk(
  'project/company/config_options/analysis/get',
  async (payload: { config_id?: string }, thunkAPI) => {
    try {
      const { config_id } = payload;
      const queryString = new URLSearchParams({
        ...(config_id && { config_id: config_id }),
      }).toString();
      
      const response = await api.get(`/project/company/config_options/analysis?${queryString}`);
      const { status, data } = response;
      
      if (status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue({ response, data });
      }
    } catch (error: any) {
      if (error.response) {
        const { status, data } = error.response;
        let errorMessage = `Request failed with status ${status}`;
        if (data && data.message) {
          errorMessage += `: ${data.message}`;
        }
        return thunkAPI.rejectWithValue({ message: errorMessage });
      } else if (error.request) {
        return thunkAPI.rejectWithValue({ message: 'No response received' });
      } else {
        return thunkAPI.rejectWithValue({ message: error.message });
      }
    }
  }
);

const configAnalyticsSlice = createSlice({
  name: 'configAnalytics',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getConfigAnalyticsStages.pending, (state) => {
        state.getAvailableStagesStatus = STATUS.PENDING;
        state.error = null;
      })
      .addCase(getConfigAnalyticsStages.fulfilled, (state, action) => {
        state.getAvailableStagesStatus = STATUS.SUCESS;
        state.availableStages = action.payload?.data?.available_stages ?? [];
        state.error = null;
      })
      .addCase(getConfigAnalyticsStages.rejected, (state, action: any) => {
        state.getAvailableStagesStatus = STATUS.FAILED;
        state.error = action.payload?.message || 'Failed to fetch config analytics stages';
      })

      .addCase(getConfigAnalytics.pending, (state) => {
        state.getConfigAnalyticsStatus = STATUS.PENDING;
        state.error = null;
      })
      .addCase(getConfigAnalytics.fulfilled, (state, action) => {
        state.getConfigAnalyticsStatus = STATUS.SUCESS;
        state.configAnalytics = action.payload.data || [];
        state.error = null;
      })
      .addCase(getConfigAnalytics.rejected, (state, action: any) => {
        state.getConfigAnalyticsStatus = STATUS.FAILED;
        state.error = action.payload?.message || 'Failed to fetch config analytics';
      })

      .addCase(getAdjudicationConfigAnalytics.pending, (state) => {
        state.getAdjudicationConfigAnalyticsStatus = STATUS.PENDING;
        state.error = null;
      })
      .addCase(getAdjudicationConfigAnalytics.fulfilled, (state, action) => {
        state.getAdjudicationConfigAnalyticsStatus = STATUS.SUCESS;
        state.adjudicationConfigAnalytics = action.payload.data?.results ?? action.payload.data ?? [];
        state.error = null;
      })
      .addCase(getAdjudicationConfigAnalytics.rejected, (state, action: any) => {
        state.getAdjudicationConfigAnalyticsStatus = STATUS.FAILED;
        state.error = action.payload?.message || 'Failed to fetch adjudication config analytics';
      })

      .addCase(getErrorInsightStages.pending, (state) => {
        state.getErrorInsightStagesStatus = STATUS.PENDING;
        state.error = null;
      })
      .addCase(getErrorInsightStages.fulfilled, (state, action) => {
        state.getErrorInsightStagesStatus = STATUS.SUCESS;
        state.errorInsightStages = (action.payload?.results ?? []).map((stage: any) => ({
          id: stage.id,
          name: stage.name,
          category: stage.category,
        }));
        state.error = null;
      })
      .addCase(getErrorInsightStages.rejected, (state, action: any) => {
        state.getErrorInsightStagesStatus = STATUS.FAILED;
        state.error = action.payload?.message || 'Failed to fetch error insight stages';
      });
  },
});

export default configAnalyticsSlice.reducer;
